import styled, {keyframes} from 'styled-components'


const skeletonShimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`

const Style = {
	SkeletonBox: styled.div`
		width: ${({$width}) => $width || '100%'};
		height: ${({$height}) => $height || '100%'};
		border-radius: ${({$borderRadius}) => $borderRadius || '8px'};
		background: linear-gradient(
			90deg,
			rgba(255, 255, 255, 0.03) 25%,
			rgba(255, 255, 255, 0.08) 50%,
			rgba(255, 255, 255, 0.03) 75%
		);
		background-size: 200% 100%;
		animation: ${skeletonShimmer} 2s infinite linear;
	`,
	Wrapper: styled.div`
		width: 100%;
		margin: 0 auto;
		position: fixed;
		bottom: 0;
		left: 0;
		width: 100dvw;
		height: 100dvh;
		z-index: 100;
		background-color: rgba(0, 0, 0, 0.5);
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		color: ${({theme}) => theme.color['white']};
	`,
	Container: styled.div`
		width: 100%;
		max-width: 768px;
		margin: 0 auto;
		border-radius: 20px 20px 0 0;
		height: 92dvh;
		position: absolute;
		overflow: auto;
		bottom: 0;
		left: 50%;
		background-color: rgb(4, 11, 22);
		transform: translate(-50%, 0);
		background-image: url(${({$bgImage}) => $bgImage});
		background-size: cover;
		background-position: center;
	`,
	Header: styled.div`
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 24px 20px 0px 20px;
		position: sticky;
		top: 0;
		background: ${({$isScrolled}) =>
			$isScrolled
				? 'rgba(39, 45, 56, 0.7)'
				: 'linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%)'};
		${({$isScrolled}) =>
			$isScrolled &&
			`
			backdrop-filter: blur(15px);
			-webkit-backdrop-filter: blur(15px);
			box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
			padding-bottom: 24px;
		`}
		z-index: 2;
		transition:
			background 0.2s ease,
			backdrop-filter 0.2s ease,
			box-shadow 0.2s ease;

		& > h2 {
			font-family: Pretendard;
			font-weight: 600;
			font-size: 16px;
			line-height: 1.125em;
			letter-spacing: -0.5%;
			color: ${({theme}) => theme.color['white']};
		}

		& > img {
			width: 14px;
			height: 14px;
			cursor: pointer;
			filter: invert(1);
		}
	`,
	Content: styled.div`
		padding: 0 20px 24px 20px;
		display: flex;
		flex-direction: column;
		gap: 24px;
	`,
	TierGrid: styled.div`
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 16px;
	`,
	TierCard: styled.div`
		background: rgba(255, 255, 255, 0.09);
		border: ${({$challenger}) =>
			$challenger
				? '1px solid transparent'
				: '0.5px solid rgba(255, 255, 255, 0.2)'};
		${({$challenger}) =>
			$challenger &&
			`
            border-image: linear-gradient(90deg, rgba(226, 0, 0, 1) 0%, rgba(255, 255, 255, 1) 30%, rgba(255, 255, 255, 1) 60%, rgba(124, 0, 0, 1) 100%) 1;
        `}
		box-shadow: ${({$challenger}) =>
			$challenger
				? '0px 0px 10px 0px rgba(226, 0, 0, 0.25)'
				: 'inset 0px 0px 4px 0px rgba(255, 255, 255, 0.25)'};
		border-radius: 12px;
		padding: 10px 6px 16px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 4px;

		& > img {
			width: 80px;
			height: 80px;
		}

		& > h3 {
			font-family: Pretendard;
			font-size: 12px;
			font-weight: 600;
			line-height: 1.193em;
			text-transform: uppercase;
			color: ${({$color, theme}) => $color || theme.color['white']};
		}

		& > p {
			font-family: Pretendard;
			font-size: 12px;
			font-weight: 500;
			line-height: 1.193em;
			color: ${({$isAltColor}) => ($isAltColor ? '#FFFFFF' : '#E0E0E0')};
		}
	`,
	TierNote: styled.div`
		font-family: Pretendard;
		font-size: 7px;
		font-weight: 400;
		line-height: 1.193em;
		color: #e0e0e0;
		text-align: center;
		margin-top: 4px;
		word-break: keep-all;
	`,
	SectionTitle: styled.div`
		display: flex;
		justify-content: center;
		align-items: center;
		margin-bottom: 12px;

		& > h3 {
			font-family: Pretendard;
			font-size: 16px;
			font-weight: 600;
			line-height: 1.193em;
			text-transform: uppercase;
			color: #ffffff;
		}
	`,
	FormulaCard: styled.div`
		background: rgba(255, 255, 255, 0.09);
		border: 1px solid rgba(255, 255, 255, 0.2);
		border-radius: 12px;
		padding: 16px 12px;
		display: flex;
		flex-direction: column;
		gap: 12px;
	`,
	FormulaHeader: styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
		margin-bottom: 8px;

		& > h3 {
			font-family: Pretendard;
			font-size: 16px;
			font-weight: 600;
			line-height: 1.193em;
			color: #ffffff;
		}

		& > p {
			font-family: Pretendard;
			font-size: 14px;
			font-weight: 600;
			letter-spacing: 0.033em;
			color: #e0e0e0;

			.red {
				color: #ed3241;
			}
		}
	`,
	FormulaVars: styled.div`
		display: flex;
		flex-direction: column;
		gap: 10px;

		& > p {
			font-family: Pretendard;
			font-size: 11px;
			font-weight: 600;
			line-height: 1.4em;
			color: #e0e0e0;

			.red {
				color: #ed3241;
			}
		}

		.white {
			color: #ffffff;
		}
	`,
	MathRow: styled.div`
		display: flex;
		flex-direction: column;
		gap: 4px;
		align-items: center;
		width: 100%;
		padding: 4px 0;
	`,
	MathEquation: styled.div`
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;

		& > span {
			font-family: 'Times New Roman';
			font-weight: 700;
			font-size: 14px;
			line-height: 1.15em;
			color: #e0e0e0;
		}

		i {
			font-style: italic;
		}
	`,
	Fraction: styled.div`
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 2px;
	`,
	Numerator: styled.span`
		font-family: Pretendard;
		font-weight: 400;
		font-size: 10px;
		line-height: 1.193em;
		color: #e0e0e0;

		&.small {
			font-size: 8px;
		}
	`,
	Denominator: styled.span`
		font-family: Pretendard;
		font-weight: 400;
		font-size: 10px;
		line-height: 1.193em;
		color: #e0e0e0;
		border-top: 0.5px solid #e0e0e0;
		padding-top: 2px;
		width: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 2px;

		&.small {
			font-size: 8px;
		}
	`,
	Superscript: styled.div`
		display: flex;
		align-items: center;
		transform: translateY(-4px);
		gap: 2px;

		& > span {
			font-family: Pretendard;
			font-weight: 300;
			font-size: 16px;
			line-height: 1.193em;
			color: #e0e0e0;
		}
	`,
	WeightTable: styled.div`
		background: rgba(39, 45, 56, 0.7);
		backdrop-filter: blur(15px);
		-webkit-backdrop-filter: blur(15px);
		box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
		border: 1px solid rgba(255, 255, 255, 0.15);
		border-radius: 12px;
		padding: 20px;
		padding: 16px 8px;
		display: flex;
		flex-direction: column;
		gap: 12px;
	`,
	WeightHeader: styled.div`
		display: flex;
		justify-content: center;
		align-items: center;

		& > h3 {
			font-family: Pretendard;
			font-size: 16px;
			font-weight: 600;
			line-height: 1.193em;
			color: #ffffff;
		}
	`,
	WeightList: styled.div`
		display: flex;
		flex-direction: column;
		gap: 8px;
	`,
	WeightRow: styled.div`
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 4px 8px;
		background:
			linear-gradient(90deg, rgba(223, 44, 44, 1) 0%, rgba(0, 0, 0, 0) 100%)
				padding-box,
			linear-gradient(90deg, rgba(184, 36, 36, 1) 0%, rgba(93, 93, 93, 0) 88%)
				border-box;
		border: 1px solid transparent;
		border-radius: 4px;

		& > div {
			display: flex;
			align-items: center;
			gap: 4px;
		}

		& h4 {
			font-family: Pretendard;
			font-size: 14px;
			font-weight: 600;
			color: #e0e0e0;
		}

		& p {
			font-family: Pretendard;
			font-size: 14px;
			font-weight: 600;
			color: #ed3241;
		}
	`,
	Star: styled.div`
		width: 100%;
		display: flex;
		justify-content: end;
	`,
	ConditionCard: styled.div`
		background: rgba(255, 255, 255, 0.05);
		border: 1px solid rgba(255, 255, 255, 0.2);
		border-radius: 12px;
		padding: 16px 12px;
		display: flex;
		flex-direction: column;
		gap: 12px;
	`,
	ConditionHeader: styled.div`
		display: flex;
		justify-content: center;
		align-items: center;
		margin-bottom: 4px;

		& > h3 {
			font-family: Pretendard;
			font-size: 16px;
			font-weight: 600;
			line-height: 1.193em;
			color: #ffffff;
		}
	`,
	ConditionList: styled.div`
		display: flex;
		flex-direction: column;
		gap: 12px;
	`,
	ConditionRow: styled.div`
		display: flex;
		align-items: flex-start;
		gap: 8px;

		& > img,
		& > svg {
			width: 14px;
			height: 14px;
			flex-shrink: 0;
			margin-top: 1px;
		}

		& > p {
			font-family: Pretendard;
			font-size: 12px;
			font-weight: 400;
			line-height: 1.4em;
			color: #e0e0e0;
			word-break: keep-all;

			strong {
				color: #ffffff;
				font-weight: 600;
			}
		}
	`,
}

export default Style
